import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, OverlayView, useJsApiLoader, Polyline } from "@react-google-maps/api";
import {
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CookLiveLocationService, {
  RecentCookLocationResponse,
  VisitInfo,
} from "../../services/CookLiveLocationService";
import { dummyCookLocations, USE_DUMMY_DATA } from "./dummyCookData";

type CookLocation = {
  id: number;
  name: string;
  cookId: number;
  lat: number;
  lng: number;
  isMoving: boolean;
  lastUpdated: string;
  rawLastUpdated: string | null;
  prevLat?: number;
  prevLng?: number;
  ongoingVisit: VisitInfo | null;
  nextVisit: VisitInfo | null;
};

type CookLocationWithOffset = CookLocation & {
  offsetX: number;
  offsetY: number;
};

type CookRoute = {
  cookId: number;
  animStart: google.maps.LatLngLiteral;    
  animEnd: google.maps.LatLngLiteral;     
  destinationPath: google.maps.LatLngLiteral[]; 
  fadingPath: google.maps.LatLngLiteral[];       
  progress: number;
  isAnimating: boolean;
  color: string;
};

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 80px)",
  minHeight: "400px",
};

const SIDEBAR_WIDTH = 320;
const ANIMATION_DURATION_MS = 8000; 
const STATIC_PATH_COLOR = "#2563EB";

const COOK_ROUTE_COLORS = [
  "#E53E3E", "#DD6B20", "#D69E2E", "#38A169", "#319795",
  "#3182CE", "#6B46C1", "#D53F8C", "#00B5D8", "#48BB78",
  "#F6AD55", "#FC8181", "#68D391", "#63B3ED", "#B794F4",
];

const getCookColor = (cookId: number): string =>
  COOK_ROUTE_COLORS[cookId % COOK_ROUTE_COLORS.length];

const STATIC_COLOR = "#1d3557";
const DEFAULT_CENTER = { lat: 28.44918, lng: 77.030228 };

const getInitialMapState = (): { center: google.maps.LatLngLiteral; zoom: number } | null => {
  try {
    const saved = sessionStorage.getItem("cookMapState");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const NEARBY_COORDINATE_THRESHOLD = 0.00012;
type FilterType = "all" | "moving" | "stationary";


const formatLastUpdated = (isoDate: string | null) => {
  if (!isoDate) return "N/A";
  const diffInMs = Date.now() - new Date(isoDate).getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hr ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
};

const getCookStatusLabel = (cook: CookLocation): string => {
  if (cook.isMoving) return "Travelling";
  if (cook.ongoingVisit) return "Ongoing";
  if (cook.nextVisit) return `Next visit at ${cook.nextVisit.time}`;
  return "Free";
};

const getCookStatusStyle = (cook: CookLocation): { bg: string; color: string } => {
  if (cook.isMoving) return { bg: "#dcfce7", color: "#16a34a" };
  if (cook.ongoingVisit) return { bg: "#dbeafe", color: "#1d4ed8" };
  if (cook.nextVisit) return { bg: "#fef3c7", color: "#92400e" };
  return { bg: "#f3f4f6", color: "#6b7280" };
};

const haversineDistanceKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const parseVisitTimeMs = (timeStr: string): number => {
  const parts = timeStr.split(" ");
  const period = parts[1] ?? "AM";
  const [hStr, mStr] = (parts[0] ?? "0:0").split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.getTime();
};

const isLateForVisit = (cook: CookLocation): boolean => {
  if (cook.isMoving || !cook.nextVisit || cook.ongoingVisit) return false;
  try {
    const visitMs = parseVisitTimeMs(cook.nextVisit.time);
    const timeLeftMin = (visitMs - Date.now()) / 60000;
    if (timeLeftMin < 0 || timeLeftMin >= 30) return false;
    const dist = haversineDistanceKm(
      cook.lat, cook.lng,
      cook.nextVisit.houseCoordinates.lat,
      cook.nextVisit.houseCoordinates.long
    );
    const reachableKm = timeLeftMin * (20 / 60); // assume 20 km/h by bike
    return dist > reachableKm;
  } catch {
    return false;
  }
};

const computeBearing = (
  from: google.maps.LatLngLiteral,
  to: google.maps.LatLngLiteral
): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLng = toRad(to.lng - from.lng);
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

// Google Maps style destination teardrop pin — fixed screen size, does not scale with map zoom
const DestinationPin = ({ color }: { color: string }) => (
  <svg width="22" height="30" viewBox="0 0 30 42" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
    <path
      d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 27 15 27S30 26.25 30 15C30 6.72 23.28 0 15 0z"
      fill={color}
      stroke="white"
      strokeWidth="1.5"
    />
    <circle cx="15" cy="15" r="6" fill="white" />
    <circle cx="15" cy="15" r="3" fill={color} />
  </svg>
);

const BikeIcon = ({ rotation, blinking, zoom }: { rotation: number; blinking: boolean; zoom: number }) => {
  const size = Math.max(48, Math.min(96, 4 * Math.pow(2, zoom - 14)));
  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.35))",
        // animation: blinking ? "bikeBlink 1s ease-in-out infinite" : "none",
      }}
    >
      <Box
        component="img"
        src="/bike-top.png"
        alt="bike"
        sx={{
          width: size,
          height: size,
          objectFit: "contain",
          transform: `rotate(${rotation}deg)`,
          transition: "transform 0.4s ease-out",
          display: "block",
        }}
      />
    </Box>
  );
};

// Returns a straight-line path [currentPos → nextHouse]. Free — no API call.
const getDestPath = (cook: CookLocation): google.maps.LatLngLiteral[] => {
  if (!cook.nextVisit?.houseCoordinates) return [];
  return [
    { lat: cook.lat, lng: cook.lng },
    { lat: cook.nextVisit.houseCoordinates.lat, lng: cook.nextVisit.houseCoordinates.long },
  ];
};

const fmtDist = (km: number): string =>
  km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

const mapCookLocation = (cook: RecentCookLocationResponse): CookLocation | null => {
  if (!cook.currentLocation?.coordinates || cook.currentLocation.coordinates.length < 2) return null;
  const [lng, lat] = cook.currentLocation.coordinates;
  const result: CookLocation = {
    id: cook.cookId,
    name: cook.cookName,
    cookId: cook.cookId,
    lat,
    lng,
    isMoving: cook.isCookMoving,
    lastUpdated: formatLastUpdated(cook.currentLocationCreatedAt),
    rawLastUpdated: cook.currentLocationCreatedAt,
    ongoingVisit: cook.ongoingVisit,
    nextVisit: cook.nextVisit,
  };
  if (cook.previousLocation?.coordinates && cook.previousLocation.coordinates.length >= 2) {
    const [prevLng, prevLat] = cook.previousLocation.coordinates;
    result.prevLat = prevLat;
    result.prevLng = prevLng;
  }
  return result;
};


const applyPathOffset = (
  path: google.maps.LatLngLiteral[],
  offsetMeters: number
): google.maps.LatLngLiteral[] => {
  if (path.length < 2 || offsetMeters === 0) return path;
  const first = path[0];
  const last = path[path.length - 1];
  const bearing = computeBearing(first, last);
  const perpRad = ((bearing + 90) * Math.PI) / 180;
  const latOff = (offsetMeters / 111000) * Math.cos(perpRad);
  const lngOff =
    (offsetMeters / (111000 * Math.cos((first.lat * Math.PI) / 180))) * Math.sin(perpRad);
  return path.map((p) => ({ lat: p.lat + latOff, lng: p.lng + lngOff }));
};

const getCookGroupKey = (cook: CookLocation) => {
  const latBucket = Math.round(cook.lat / NEARBY_COORDINATE_THRESHOLD);
  const lngBucket = Math.round(cook.lng / NEARBY_COORDINATE_THRESHOLD);
  return `${latBucket}:${lngBucket}`;
};

const getCooksWithOffsets = (cooks: CookLocation[]): CookLocationWithOffset[] => {
  const groupedCooks = new Map<string, CookLocation[]>();
  cooks.forEach((cook) => {
    const key = getCookGroupKey(cook);
    const existing = groupedCooks.get(key) || [];
    existing.push(cook);
    groupedCooks.set(key, existing);
  });
  return Array.from(groupedCooks.values()).flatMap((group) => {
    const sortedGroup = [...group].sort((a, b) => a.cookId - b.cookId);
    const groupSize = sortedGroup.length;
    return sortedGroup.map((cook, index) => {
      if (index === 0 || groupSize === 1) return { ...cook, offsetX: 0, offsetY: 0 };
      const ring = Math.floor((index - 1) / 8) + 1;
      const positionInRing = (index - 1) % 8;
      const angle = (positionInRing * 45 * Math.PI) / 180;
      const radius = 28 + (ring - 1) * 20;
      return { ...cook, offsetX: Math.cos(angle) * radius, offsetY: Math.sin(angle) * radius };
    });
  });
};

function CookLiveLocation() {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
  });

  const navigate = useNavigate();
  const cookLiveLocationService = useMemo(() => new CookLiveLocationService(), []);

  const [hoveredCook, setHoveredCook] = useState<CookLocation | null>(null);
  const [hoveredDestPin, setHoveredDestPin] = useState<{cook: CookLocation, visitType: 'ongoing' | 'next'} | null>(null);
  const [cooks, setCooks] = useState<CookLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cookRoutes, setCookRoutes] = useState<Map<number, CookRoute>>(new Map());
  const [filter, setFilter] = useState<FilterType>("all");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mapZoom, setMapZoom] = useState<number>(16);
  // Stationary cook path on click
  const [stationaryRoutes, setStationaryRoutes] = useState<Map<number, google.maps.LatLngLiteral[]>>(new Map());
  const [clickedStationaryCookId, setClickedStationaryCookId] = useState<number | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const filterTriggeredRef = useRef(false);
  const prevCooksRef = useRef<Map<number, { lat: number; lng: number }>>(new Map());
  const cookRoutesRef = useRef<Map<number, CookRoute>>(new Map());

  // Read synchronously so defaultCenter/defaultZoom are correct on first render
  const mapStateRef = useRef<{ center: google.maps.LatLngLiteral; zoom: number } | null>(getInitialMapState());
  const _savedCookId = sessionStorage.getItem("cookLastViewedId");
  const pendingHighlightCookIdRef = useRef<number | null>(_savedCookId ? parseInt(_savedCookId, 10) : null);

  const getTooltipTransform = (lat: number, lng: number): string => {
    if (!mapRef.current) return "translate(-50%, calc(-100% - 18px))";
    const bounds = mapRef.current.getBounds();
    if (!bounds) return "translate(-50%, calc(-100% - 18px))";
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const latRange = ne.lat() - sw.lat();
    const lngRange = ne.lng() - sw.lng();
    const isNearTop = lat > sw.lat() + 0.75 * latRange;
    const isNearLeft = lng < sw.lng() + 0.2 * lngRange;
    const isNearRight = lng > sw.lng() + 0.8 * lngRange;
    const yT = isNearTop ? "20px" : "calc(-100% - 18px)";
    const xT = isNearLeft ? "0" : isNearRight ? "-100%" : "-50%";
    return `translate(${xT}, ${yT})`;
  };

  const saveMapState = () => {
    if (!mapRef.current) return;
    const center = mapRef.current.getCenter();
    const zoom = mapRef.current.getZoom();
    if (!center || zoom == null) return;
    const state = { center: { lat: center.lat(), lng: center.lng() }, zoom };
    mapStateRef.current = state;
    sessionStorage.setItem("cookMapState", JSON.stringify(state));
  };

  const clearHoverCloseTimeout = () => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  };

  const openCookDetails = (cook: CookLocation) => {
    clearHoverCloseTimeout();
    setHoveredCook(cook);
  };

  const closeCookDetailsWithDelay = () => {
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(() => setHoveredCook(null), 180);
  };

  // Click handler for moving cooks (sidebar)
  const handleMovingCookClick = (cook: CookLocation) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat: cook.lat, lng: cook.lng });
      mapRef.current.setZoom(18);
    }
    openCookDetails(cook);
  };

  // Click handler for stationary cooks — zooms in, draws straight line to next visit house
  const handleStationaryClick = (cook: CookLocation) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat: cook.lat, lng: cook.lng });
      mapRef.current.setZoom(18);
    }
    openCookDetails(cook);

    // No next visit — just zoom, no path to draw
    if (!cook.nextVisit?.houseCoordinates) {
      setClickedStationaryCookId(null);
      return;
    }

    // Toggle: clicking the same cook clears the path
    if (clickedStationaryCookId === cook.cookId) {
      setClickedStationaryCookId(null);
      return;
    }

    setClickedStationaryCookId(cook.cookId);

    // Straight line — free, no Directions API call
    setStationaryRoutes((prev) => {
      const next = new Map(prev);
      next.set(cook.cookId, getDestPath(cook));
      return next;
    });
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const fetchData = async () => {
      let response: RecentCookLocationResponse[];
      if (USE_DUMMY_DATA) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        response = dummyCookLocations;
      } else {
        response = await cookLiveLocationService.getRecentCookLocations();
      }
      if (!isMounted) return;

      const mappedCooks = response
        .map(mapCookLocation)
        .filter((cook): cook is CookLocation => cook !== null);

      setCooks(mappedCooks);
      setLastRefresh(new Date());
      setHoveredCook((current) => {
        if (pendingHighlightCookIdRef.current !== null) {
          const cookToHighlight = mappedCooks.find((c) => c.cookId === pendingHighlightCookIdRef.current);
          if (cookToHighlight) {
            pendingHighlightCookIdRef.current = null;
            sessionStorage.removeItem("cookLastViewedId");
            return cookToHighlight;
          }
        }
        return current ? mappedCooks.find((cook) => cook.cookId === current.cookId) || null : null;
      });
      setLoading(false);
    };

    fetchData();
    const intervalId = setInterval(fetchData, 10000);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [cookLiveLocationService]);

  // Straight-line routes — no Directions API, fully synchronous, zero cost
  const calculateRoutes = useCallback((cooksData: CookLocation[]) => {
    const newRoutes = new Map<number, CookRoute>();
    const movingCooks = cooksData.filter((cook) => cook.isMoving);
    let hasNewAnimations = false;

    for (const cook of movingCooks) {
      const prevPosition = prevCooksRef.current.get(cook.cookId);
      const existingRoute = cookRoutesRef.current.get(cook.cookId);
      const positionChanged =
        !prevPosition || prevPosition.lat !== cook.lat || prevPosition.lng !== cook.lng;

      // Re-use existing path only if position hasn't changed AND we already have a valid path
      if (!positionChanged && existingRoute && existingRoute.destinationPath.length > 1) {
        newRoutes.set(cook.cookId, { ...existingRoute, isAnimating: false, progress: 1, fadingPath: [] });
        continue;
      }

      const destPath = getDestPath(cook); // straight line, free

      if (!prevPosition || !positionChanged) {
        newRoutes.set(cook.cookId, {
          cookId: cook.cookId,
          animStart: { lat: cook.lat, lng: cook.lng },
          animEnd: { lat: cook.lat, lng: cook.lng },
          destinationPath: destPath,
          fadingPath: [],
          progress: 1,
          isAnimating: false,
          color: existingRoute?.color ?? getCookColor(cook.cookId),
        });
      } else {
        // Position changed — animate marker smoothly from prevPos to current
        newRoutes.set(cook.cookId, {
          cookId: cook.cookId,
          animStart: { lat: prevPosition.lat, lng: prevPosition.lng },
          animEnd: { lat: cook.lat, lng: cook.lng },
          destinationPath: destPath,
          fadingPath: existingRoute?.destinationPath ?? [],
          progress: 0,
          isAnimating: true,
          color: existingRoute?.color ?? getCookColor(cook.cookId),
        });
        hasNewAnimations = true;
      }
    }

    cooksData.forEach((cook) => {
      prevCooksRef.current.set(cook.cookId, { lat: cook.lat, lng: cook.lng });
    });

    setCookRoutes(newRoutes);
    if (hasNewAnimations) {
      lastFrameTimeRef.current = null;
    }
  }, []);

  useEffect(() => {
    cookRoutesRef.current = cookRoutes;
  }, [cookRoutes]);

  useEffect(() => {
    if (cooks.length > 0 && isLoaded) {
      calculateRoutes(cooks);
    }
  }, [cooks, isLoaded, calculateRoutes]);

  // Animation tick — linear interpolation of marker between animStart and animEnd
  useEffect(() => {
    const tick = (timestamp: number) => {
      if (lastFrameTimeRef.current === null) lastFrameTimeRef.current = timestamp;
      const delta = timestamp - lastFrameTimeRef.current;
      lastFrameTimeRef.current = timestamp;
      const progressIncrement = delta / ANIMATION_DURATION_MS;

      setCookRoutes((prevRoutes) => {
        let hasAnimating = false;
        const updated = new Map(prevRoutes);

        updated.forEach((route, key) => {
          if (route.isAnimating) {
            const newProgress = Math.min(route.progress + progressIncrement, 1);
            updated.set(key, {
              ...route,
              progress: newProgress,
              isAnimating: newProgress < 1,
              // Clear fadingPath once animation completes
              fadingPath: newProgress >= 1 ? [] : route.fadingPath,
            });
            if (newProgress < 1) hasAnimating = true;
          }
        });

        if (hasAnimating) {
          animationFrameRef.current = requestAnimationFrame(tick);
        }
        return updated;
      });
    };

    const hasAnimating = Array.from(cookRoutes.values()).some((r) => r.isAnimating);
    if (hasAnimating) {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [cookRoutes]);

  const mapCenterRef = useRef(DEFAULT_CENTER);
  const defaultCenter = useMemo(() => {
    if (mapStateRef.current?.center) return mapStateRef.current.center;
    if (cooks.length && mapCenterRef.current === DEFAULT_CENTER) {
      const total = cooks.reduce(
        (acc, cook) => ({ lat: acc.lat + cook.lat, lng: acc.lng + cook.lng }),
        { lat: 0, lng: 0 }
      );
      mapCenterRef.current = { lat: total.lat / cooks.length, lng: total.lng / cooks.length };
    }
    return mapCenterRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultZoom = mapStateRef.current?.zoom ?? 16;

  const movingCount = cooks.filter((cook) => cook.isMoving).length;

  const filteredCooks = useMemo(() => {
    if (filter === "moving") return cooks.filter((c) => c.isMoving);
    if (filter === "stationary") return cooks.filter((c) => !c.isMoving);
    return cooks;
  }, [cooks, filter]);

  useEffect(() => {
    if (!filterTriggeredRef.current) return;
    if (!mapRef.current || !window.google || filteredCooks.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    filteredCooks.forEach((cook) => bounds.extend({ lat: cook.lat, lng: cook.lng }));
    mapRef.current.fitBounds(bounds);

    const listener = window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      saveMapState();
      filterTriggeredRef.current = false;
    });

    return () => { window.google.maps.event.removeListener(listener); };
  }, [filter, filteredCooks]);

  const sidebarFilteredCooks = useMemo(() => {
    let result = filteredCooks;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(query));
    }
    return result.sort((a, b) => {
      if (a.isMoving && !b.isMoving) return -1;
      if (!a.isMoving && b.isMoving) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredCooks, searchQuery]);

  const cooksWithOffsets = useMemo(() => getCooksWithOffsets(filteredCooks), [filteredCooks]);

  if (!isLoaded) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
        <Typography fontWeight={600}>Loading cook live locations...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        "@keyframes bikeBlink": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0.3 },
          "100%": { opacity: 1 },
        },
      }}
    >

      <Box sx={{ display: "flex", flexDirection: "row", height: containerStyle.height }}>
        {/* Map Area */}
        <Box sx={{ flex: 1, position: "relative" }}>
          {/* Loading overlay */}
          {loading && cooks.length === 0 && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 2000, bgcolor: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ bgcolor: "#ffffff", px: 3, py: 1.5, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
                <Typography fontWeight={600} fontSize="0.9rem">Loading cook locations...</Typography>
              </Box>
            </Box>
          )}
          {/* Filter Buttons */}
          <Box
            sx={{
              position: "absolute",
              top: 5,
              left: 200,
              zIndex: 1000,
              display: "flex",
              gap: 1,
              bgcolor: "rgba(255,255,255,0.95)",
              p: 1,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {[
              { key: "all" as FilterType, label: `All (${cooks.length})` },
              { key: "moving" as FilterType, label: `Moving (${movingCount})` },
              { key: "stationary" as FilterType, label: `Stationary (${cooks.length - movingCount})` },
            ].map(({ key, label }) => (
              <Box
                key={key}
                onClick={() => { filterTriggeredRef.current = true; setClickedStationaryCookId(null); setFilter(key); }}
                sx={{
                  px: 2, py: 0.75, borderRadius: "6px", cursor: "pointer",
                  backgroundColor: filter === key ? "#4F46E5" : "#f1f5f9",
                  color: filter === key ? "#ffffff" : "#475569",
                  border: "1px solid",
                  borderColor: filter === key ? "#4F46E5" : "#e2e8f0",
                  transition: "all 0.15s ease",
                  fontSize: "0.85rem", fontWeight: 500, whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: filter === key ? "#4338ca" : "#e2e8f0" },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={defaultCenter}
            zoom={defaultZoom}
            onLoad={(map) => { mapRef.current = map; }}
            onZoomChanged={() => {
              if (mapRef.current) {
                setMapZoom(mapRef.current.getZoom() ?? 16);
                saveMapState();
              }
            }}
            onDragEnd={saveMapState}
          >
            {/* Fading old paths for moving cooks (behind bike as it moves) */}
            {filter !== "stationary" &&
              Array.from(cookRoutes.values())
                .filter((r) => r.fadingPath.length > 1 && filteredCooks.some((c) => c.cookId === r.cookId))
                .map((r) => (
                  <Polyline
                    key={`fading-${r.cookId}`}
                    path={r.fadingPath}
                    options={{
                      strokeColor: r.color,
                      strokeOpacity: Math.max(0, 0.6 * (1 - r.progress)),
                      strokeWeight: 6,
                      zIndex: 1,
                    }}
                  />
                ))}

            {/* Current destination paths for moving cooks (current pos → next visit house) */}
            {filter !== "stationary" &&
              (() => {
                const visibleRoutes = Array.from(cookRoutes.values()).filter(
                  (r) => r.destinationPath.length > 1 && filteredCooks.some((c) => c.cookId === r.cookId)
                );
                const total = visibleRoutes.length;
                return visibleRoutes.map((r, idx) => {
                  // Spread overlapping paths: -4m, 0, +4m (or -4, +4 for 2 routes etc.)
                  const offsetMeters = total <= 1 ? 0 : (idx - (total - 1) / 2) * 5;
                  const offsetPath = applyPathOffset(r.destinationPath, offsetMeters);
                  return (
                  <Polyline
                    key={`dest-${r.cookId}`}
                    path={offsetPath}
                    options={{
                      strokeColor: r.color,
                      strokeOpacity: 1,
                      strokeWeight: 4,
                      zIndex: 2,
                      icons: [
                        {
                          icon: {
                            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 4,
                            fillColor: "#ffffff",
                            fillOpacity: 1,
                            strokeColor: r.color,
                            strokeWeight: 1,
                          },
                          offset: "50%",
                          repeat: "120px",
                        },
                      ],
                    }}
                  />
                  );
                });
              })()}

            {/* Stationary cook path — drawn on click, blue */}
            {clickedStationaryCookId !== null &&
              stationaryRoutes.has(clickedStationaryCookId) &&
              stationaryRoutes.get(clickedStationaryCookId)!.length > 1 && (
                <Polyline
                  key={`stat-${clickedStationaryCookId}`}
                  path={stationaryRoutes.get(clickedStationaryCookId)!}
                  options={{
                    strokeColor: STATIC_PATH_COLOR,
                    strokeOpacity: 1,
                    strokeWeight: 7,
                    zIndex: 2,
                    icons: [
                      {
                        icon: {
                          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          scale: 4,
                          fillColor: "#ffffff",
                          fillOpacity: 1,
                          strokeColor: STATIC_PATH_COLOR,
                          strokeWeight: 1,
                        },
                        offset: "50%",
                        repeat: "120px",
                      },
                    ],
                  }}
                />
              )}

            {/* Destination pins — end of each moving cook's route */}
            {filter !== "stationary" &&
              Array.from(cookRoutes.values())
                .filter((r) => r.destinationPath.length > 1 && filteredCooks.some((c) => c.cookId === r.cookId))
                .map((r) => {
                  const dest = r.destinationPath[r.destinationPath.length - 1];
                  const cook = filteredCooks.find((c) => c.cookId === r.cookId);
                  return (
                    <OverlayView
                      key={`dest-pin-${r.cookId}`}
                      position={dest}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Box
                        sx={{ transform: "translate(-50%, -100%)" }}
                        onMouseEnter={() => cook && setHoveredDestPin({ cook, visitType: cook.ongoingVisit ? 'ongoing' : 'next' })}
                        onMouseLeave={() => setHoveredDestPin(null)}
                      >
                        <DestinationPin color={r.color} />
                      </Box>
                    </OverlayView>
                  );
                })}

            {/* Destination pin for clicked stationary cook */}
            {clickedStationaryCookId !== null &&
              stationaryRoutes.has(clickedStationaryCookId) &&
              stationaryRoutes.get(clickedStationaryCookId)!.length > 1 && (
                (() => {
                  const cook = filteredCooks.find((c) => c.cookId === clickedStationaryCookId);
                  return (
                    <OverlayView
                      key={`stat-dest-pin-${clickedStationaryCookId}`}
                      position={stationaryRoutes.get(clickedStationaryCookId)![stationaryRoutes.get(clickedStationaryCookId)!.length - 1]}
                      mapPaneName={OverlayView.OVERLAY_LAYER}
                    >
                      <Box
                        sx={{ transform: "translate(-50%, -100%)" }}
                        onMouseEnter={() => cook && setHoveredDestPin({ cook, visitType: 'next' })}
                        onMouseLeave={() => setHoveredDestPin(null)}
                      >
                        <DestinationPin color={STATIC_PATH_COLOR} />
                      </Box>
                    </OverlayView>
                  );
                })()
              )}

            {/* Cook markers */}
            {cooksWithOffsets.map((cook) => {
              const route = cookRoutes.get(cook.cookId);
              let animatedPosition = { lat: cook.lat, lng: cook.lng };
              let rotation = 0;

              if (route && cook.isMoving) {
                const p = route.progress;
                animatedPosition = {
                  lat: route.animStart.lat + (route.animEnd.lat - route.animStart.lat) * p,
                  lng: route.animStart.lng + (route.animEnd.lng - route.animStart.lng) * p,
                };
                if (
                  route.animStart.lat !== route.animEnd.lat ||
                  route.animStart.lng !== route.animEnd.lng
                ) {
                  rotation = computeBearing(route.animStart, route.animEnd);
                }
              }

              return (
                <OverlayView
                  key={cook.id}
                  position={animatedPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    onMouseEnter={() => openCookDetails(cook)}
                    onMouseLeave={closeCookDetailsWithDelay}
                    onClick={() => {
                        saveMapState();
                        sessionStorage.setItem("cookLastViewedId", String(cook.cookId));
                        navigate(`/Cooks/${cook.cookId}/show`);
                      
                    }}
                    sx={{
                      position: "relative",
                      transform: `translate(calc(-50% + ${cook.offsetX}px), calc(-50% + ${cook.offsetY}px))`,
                      cursor: "pointer",
                    }}
                  >
                    {cook.isMoving ? (
                      <BikeIcon
                        rotation={rotation}
                        blinking={!!route && !route.isAnimating}
                        zoom={mapZoom}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 36,
                          width: 36,
                          borderRadius: "999px",
                          bgcolor: "#ffffff",
                          color: STATIC_COLOR,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.28))",
                          border: "1.5px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <Typography fontSize="1.6rem">👨‍🍳</Typography>
                      </Box>
                    )}
                  </Box>
                </OverlayView>
              );
            })}

            {/* Hover popup */}
            {hoveredCook && (
              <OverlayView
                position={{ lat: hoveredCook.lat, lng: hoveredCook.lng }}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <Box sx={{ transform: getTooltipTransform(hoveredCook.lat, hoveredCook.lng), pointerEvents: "none" }}>
                  <Stack
                    spacing={0.5}
                    minWidth={200}
                    sx={{
                      px: 1.25, py: 1, borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.97)",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography fontWeight={700}>{hoveredCook.name}</Typography>
                    <Typography variant="body2" color="text.secondary">ID: {hoveredCook.cookId}</Typography>
                    <Typography variant="body2">
                      Status:{" "}
                      <Box component="span" fontWeight={600}>
                        {getCookStatusLabel(hoveredCook)}
                      </Box>
                    </Typography>
                    {hoveredCook.nextVisit && (
                      <Typography variant="body2">
                        Next visit:{" "}
                        <Box component="span" fontWeight={600}>{hoveredCook.nextVisit.time}</Box>
                        {" "}· House #{hoveredCook.nextVisit.houseId}
                      </Typography>
                    )}
                    {hoveredCook.ongoingVisit && (
                      <>
                        <Typography variant="body2">
                          Ongoing · House #{hoveredCook.ongoingVisit.houseId}
                          {" · "}{hoveredCook.ongoingVisit.time}
                        </Typography>
                        {hoveredCook.ongoingVisit.checkInTime && (
                          <Typography variant="body2" color="text.secondary">
                            Checked in:{" "}
                            {new Date(hoveredCook.ongoingVisit.checkInTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        )}
                      </>
                    )}
                    {!hoveredCook.isMoving && hoveredCook.nextVisit?.houseCoordinates && (
                      <Typography variant="body2">
                        Distance to next visit:{" "}
                        <Box component="span" fontWeight={600}>
                          {fmtDist(haversineDistanceKm(
                            hoveredCook.lat, hoveredCook.lng,
                            hoveredCook.nextVisit.houseCoordinates.lat,
                            hoveredCook.nextVisit.houseCoordinates.long
                          ))}
                        </Box>
                      </Typography>
                    )}
                    {!hoveredCook.ongoingVisit && !hoveredCook.nextVisit && !hoveredCook.isMoving && (
                      <Typography variant="body2" color="text.secondary">
                        No upcoming visits
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Updated: {hoveredCook.lastUpdated}
                    </Typography>
                  </Stack>
                </Box>
              </OverlayView>
            )}

            {/* Destination pin hover popup */}
            {hoveredDestPin && (() => {
              const visit = hoveredDestPin.visitType === 'ongoing' ? hoveredDestPin.cook.ongoingVisit : hoveredDestPin.cook.nextVisit;
              if (!visit) return null;
              return (
                <OverlayView
                  position={{ lat: visit.houseCoordinates.lat, lng: visit.houseCoordinates.long }}
                  mapPaneName={OverlayView.FLOAT_PANE}
                >
                  <Box sx={{ transform: getTooltipTransform(visit.houseCoordinates.lat, visit.houseCoordinates.long), pointerEvents: "none" }}>
                    <Stack
                      spacing={0.5}
                      minWidth={180}
                      sx={{
                        px: 1.25, py: 1, borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.97)",
                        boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography fontWeight={700}>{hoveredDestPin.cook.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {hoveredDestPin.visitType === 'ongoing' ? 'Ongoing visit' : 'Next visit'}
                      </Typography>
                      <Typography variant="body2">
                        Time: <Box component="span" fontWeight={600}>{visit.time}</Box>
                      </Typography>
                      <Typography variant="body2">
                        House ID: <Box component="span" fontWeight={600}>#{visit.houseId}</Box>
                      </Typography>
                    </Stack>
                  </Box>
                </OverlayView>
              );
            })()}
          </GoogleMap>
        </Box>

        {/* Right Sidebar */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            bgcolor: "#ffffff",
            borderLeft: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700}>Active Cooks</Typography>
              <Typography variant="caption" color="text.secondary">{sidebarFilteredCooks.length} of {cooks.length}</Typography>
            </Box>
            <Box sx={{ position: "relative", mb: 2 }}>
              <input
                type="text"
                placeholder="Search cooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "14px" }}>🔍</span>
            </Box>
          </Box>

          {/* Cook List */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
            {sidebarFilteredCooks.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "#94a3b8" }}>
                <Typography variant="body2">No cooks found</Typography>
              </Box>
            ) : (
              sidebarFilteredCooks.map((cook) => {
                const statusLabel = getCookStatusLabel(cook);
                const statusStyle = getCookStatusStyle(cook);
                const lateAlert = isLateForVisit(cook);

                return (
                  <Box
                    key={cook.cookId}
                    onClick={() => {
                      if (cook.isMoving) {
                        handleMovingCookClick(cook);
                      } else {
                        handleStationaryClick(cook);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      mb: 1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      backgroundColor: hoveredCook?.cookId === cook.cookId ? "#f1f5f9" : "#ffffff",
                      border: "1px solid #e2e8f0",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "#f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                    }}
                  >
                    {/* Cook Icon */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: cook.isMoving ? "#dcfce7" : "#f1f5f9",
                        border: cook.isMoving ? "2px solid #22c55e" : "2px solid transparent",
                        position: "relative",
                        flexShrink: 0,
                      }}
                    >
                      <Typography fontSize="1.3rem">{cook.isMoving ? "🛵" : "👨‍🍳"}</Typography>
                      {cook.isMoving && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -2, right: -2,
                            width: 10, height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#22c55e",
                            border: "2px solid #ffffff",
                          }}
                        />
                      )}
                      {/* Red alert dot for late stationary cooks */}
                      {lateAlert && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -3, right: -3,
                            width: 12, height: 12,
                            borderRadius: "50%",
                            backgroundColor: "#ef4444",
                            border: "2px solid #ffffff",
                          }}
                          title="May not reach next visit in time"
                        />
                      )}
                    </Box>

                    {/* Cook Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} fontSize="0.9rem" noWrap>
                        {cook.name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            px: 1, py: 0.25,
                            borderRadius: "4px",
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {statusLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          ID: {cook.cookId}
                        </Typography>
                      </Box>
                      {/* Next visit time — shown for moving cooks and ongoing cooks */}
                      {cook.nextVisit && (cook.isMoving || cook.ongoingVisit) && (
                        <Typography
                          sx={{ fontSize: "0.68rem", color: "#92400e", mt: 0.3 }}
                          noWrap
                        >
                          Next visit at {cook.nextVisit.time}
                        </Typography>
                      )}
                      {/* Clickable visit IDs in blue */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.3 }}>
                        {cook.ongoingVisit && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              saveMapState();
                              navigate(`/OneMealService/${cook.ongoingVisit!.id}/show`);
                            }}
                            sx={{
                              fontSize: "0.68rem",
                              color: "#2563EB",
                              fontWeight: 600,
                              cursor: "pointer",
                              width: "fit-content",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Ongoing #{cook.ongoingVisit.id}
                          </Box>
                        )}
                        {cook.nextVisit && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              saveMapState();
                              navigate(`/OneMealService/${cook.nextVisit!.id}/show`);
                            }}
                            sx={{
                              fontSize: "0.68rem",
                              color: "#2563EB",
                              fontWeight: 600,
                              cursor: "pointer",
                              width: "fit-content",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Next visit #{cook.nextVisit.id}
                          </Box>
                        )}
                      </Box>
                      {lateAlert && (
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            px: 0.75, py: 0.15, mt: 0.25,
                            borderRadius: "4px",
                            backgroundColor: "#fee2e2",
                            color: "#dc2626",
                            fontWeight: 600,
                            display: "inline-block",
                          }}
                        >
                          ⚠ Late for next visit
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Sidebar Footer */}
          <Box sx={{ px: 1.5, py: 0.75, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {loading && <Chip label="Loading..." size="small" sx={{ bgcolor: "#e2e8f0", height: "20px" }} />}
              {lastRefresh && (
                <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                  Updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CookLiveLocation;
